import { useEffect, useMemo, useRef, useState } from "react";
import { AtSign } from "lucide-react";

/**
 * Textarea wrapper dengan `@` autocomplete untuk mention user.
 * - Detect `@word` di cursor position → filter users by name/first-word/prefix
 * - Arrow keys ↑↓ untuk navigate, Enter/Tab untuk select, Esc untuk close
 * - Click di suggestion juga bisa
 * - Kalau selected, replace `@word` dengan `@FirstToken` (nama single-word tanpa spasi)
 *   biar konsisten dengan regex resolver di backend.
 *
 * Props:
 *   value, onChange (controlled)
 *   users: [{id, name}] — dari /api/users
 *   currentUserId — exclude dari suggestion (user tidak bisa mention diri sendiri)
 *   ...rest: forwarded ke <textarea>
 */
export default function MentionTextarea({ value, onChange, users = [], currentUserId, testid, ...rest }) {
  const ref = useRef(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [triggerStart, setTriggerStart] = useState(-1); // index dari `@` di value
  const [activeIdx, setActiveIdx] = useState(0);

  // Normalize users → { id, name, firstToken (untuk replace), searchText }
  const pool = useMemo(() => {
    return (users || [])
      .filter((u) => u && u.id && u.id !== currentUserId)
      .map((u) => {
        const name = (u.name || "").trim();
        const firstToken = name.split(/\s+/)[0] || name;
        return { id: u.id, name, firstToken, search: name.toUpperCase() };
      });
  }, [users, currentUserId]);

  const suggestions = useMemo(() => {
    if (!suggestOpen || !query) return [];
    const q = query.toUpperCase();
    // Prefix match diprioritaskan, lalu substring
    const prefix = pool.filter((p) => p.search.startsWith(q));
    const contains = pool.filter((p) => !p.search.startsWith(q) && p.search.includes(q));
    return [...prefix, ...contains].slice(0, 6);
  }, [pool, query, suggestOpen]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  // Deteksi trigger tiap kali value / cursor berubah
  const detectTrigger = () => {
    const el = ref.current;
    if (!el) return;
    const pos = el.selectionStart || 0;
    const before = (value || "").slice(0, pos);
    // Regex: `@` diikuti karakter word (bukan spasi/enter), di ujung `before`
    const m = before.match(/(?:^|\s)@([A-Za-z0-9._-]{0,30})$/);
    if (m) {
      setTriggerStart(pos - m[1].length - 1); // index of `@`
      setQuery(m[1]);
      setSuggestOpen(true);
    } else {
      setSuggestOpen(false);
      setTriggerStart(-1);
      setQuery("");
    }
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    // detectTrigger dipanggil setelah value ter-update via useEffect di bawah
    setTimeout(detectTrigger, 0);
  };
  const handleKeyUp = () => detectTrigger();
  const handleClick = () => detectTrigger();

  const applyMention = (u) => {
    if (triggerStart < 0) return;
    const before = (value || "").slice(0, triggerStart);
    const afterCursor = (value || "").slice((ref.current?.selectionStart) || 0);
    // Replace `@query` dengan `@FirstToken ` (dengan spasi supaya user tinggal ketik lanjutan)
    const inserted = `@${u.firstToken} `;
    const next = before + inserted + afterCursor;
    onChange(next);
    setSuggestOpen(false);
    setTriggerStart(-1);
    setQuery("");
    // Restore cursor tepat setelah inserted mention
    setTimeout(() => {
      const el = ref.current;
      if (el) {
        const newPos = before.length + inserted.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!suggestOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      applyMention(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        data-testid={testid}
        {...rest}
      />
      {suggestOpen && suggestions.length > 0 && (
        <ul
          data-testid="mention-suggestions"
          className="absolute z-50 left-0 right-0 sm:right-auto sm:min-w-[240px] mt-1 border border-slate-300 bg-white shadow-lg max-h-56 overflow-y-auto"
          role="listbox"
        >
          <li className="px-2 py-1 text-[9px] uppercase tracking-wider text-slate-400 font-semibold bg-slate-50 border-b border-slate-200 flex items-center gap-1">
            <AtSign size={10} /> Mention seseorang
          </li>
          {suggestions.map((u, i) => (
            <li
              key={u.id}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); applyMention(u); }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`mention-option-${u.firstToken}`}
              className={`px-2 py-1.5 cursor-pointer text-xs flex items-center gap-2 ${i === activeIdx ? "bg-[#0B1D3A] text-white" : "hover:bg-slate-100"}`}
            >
              <div className={`w-6 h-6 flex items-center justify-center text-[10px] font-bold uppercase shrink-0 ${i === activeIdx ? "bg-white text-[#0B1D3A]" : "bg-slate-200 text-slate-700"}`}>
                {u.firstToken.slice(0, 2)}
              </div>
              <span className="truncate">{u.name}</span>
              <span className={`ml-auto text-[10px] font-mono ${i === activeIdx ? "text-white/70" : "text-slate-400"}`}>@{u.firstToken}</span>
            </li>
          ))}
          <li className="px-2 py-1 text-[9px] italic text-slate-400 bg-slate-50 border-t border-slate-200">
            ↑↓ pilih · Enter/Tab masukkan · Esc batal
          </li>
        </ul>
      )}
    </div>
  );
}
